import { ConversationStatus } from '@/modules/chat/types/conversation';
import {
  ApprovalStatus,
  ChipColorVariants,
  CollectOverviewStatus,
  DataSelectionOverviewStatus,
  EsgArea,
  EsgCategoryEnum,
  EsgKpiStatus,
  FacilityType,
  ReportingFramework,
  RequestStatus,
  RiskOpportunityType,
  Status,
  Unit,
  UserGroupCategory,
  UserRole,
} from '@/modules/common/types';
import { RelatedIRO } from '@/modules/goalAndAction/types/goalActionDetail';

export const getChipColorForLetter = (text: string) => {
  const chipKeys = Object.keys(ChipColorVariants).filter(
    (chipKey) => chipKey !== 'RED' && chipKey !== 'WARM_BLUE' && chipKey !== 'MILK_WHITE'
  ) as Array<keyof typeof ChipColorVariants>;
  const letter = text.charAt(0).toUpperCase();
  const index = (letter.charCodeAt(0) - 65) % chipKeys.length;
  return ChipColorVariants[chipKeys[index] as keyof typeof ChipColorVariants];
};

export const getStatusChipColor = (status: Status) => {
  switch (status) {
    case Status.COMPLETED:
      return ChipColorVariants.LIGHT_GREEN;

    case Status.IN_PROGRESS:
      return ChipColorVariants.LIGHT_YELLOW;

    case Status.NOT_STARTED:
      return ChipColorVariants.ALMOND;
    default:
      return ChipColorVariants.RED;
  }
};

export const getCategoryChipColor = (category: EsgCategoryEnum) => {
  switch (category) {
    case EsgCategoryEnum.VEHICLE_FLEET:
      return ChipColorVariants.ALMOND;

    case EsgCategoryEnum.WATER:
      return ChipColorVariants.PURPLE;

    case EsgCategoryEnum.PURCHASED_ENERGY:
      return ChipColorVariants.LIGHT_GREEN;

    case EsgCategoryEnum.SELF_PRODUCED_ENERGY:
      return ChipColorVariants.LIGHT_YELLOW;

    case EsgCategoryEnum.EMPLOYMENT_DIVERSITY:
      return ChipColorVariants.WATER;

    case EsgCategoryEnum.HEALTH_SAFETY:
      return ChipColorVariants.TEALISH_BLUE;

    case EsgCategoryEnum.DISCRIMINATION:
      return ChipColorVariants.GREENISH_CYAN;

    case EsgCategoryEnum.TRAINING:
      return ChipColorVariants.GREENISH_YELLOW;

    case EsgCategoryEnum.WASTE:
      return ChipColorVariants.PALE_GREEN;

    case EsgCategoryEnum.AGE_STRUCTURE:
      return ChipColorVariants.ICE_BERG;

    case EsgCategoryEnum.RAW_MATERIALS:
      return ChipColorVariants.GHOST;

    case EsgCategoryEnum.FINANCIAL:
      return ChipColorVariants.JAGGED_ICE;

    case EsgCategoryEnum.GOVERNANCE_BODIES:
      return ChipColorVariants.GRAY;

    case EsgCategoryEnum.BUSINESS_CONDUCT:
      return ChipColorVariants.ALMOND;

    case EsgCategoryEnum.REMUNERATION:
      return ChipColorVariants.PURPLE;

    case EsgCategoryEnum.BIODIVERSITY:
      return ChipColorVariants.LIGHT_GREEN;

    case EsgCategoryEnum.POLLUTION:
      return ChipColorVariants.LIGHT_YELLOW;

    case EsgCategoryEnum.SUPPLY_CHAIN:
      return ChipColorVariants.WATER;

    case EsgCategoryEnum.COMPANY_BUSINESS_CONDUCT:
      return ChipColorVariants.TEALISH_BLUE;

    case EsgCategoryEnum.COMPANY_GOVERNANCE_BODIES:
      return ChipColorVariants.GREENISH_CYAN;

    case EsgCategoryEnum.COMPANY_SUPPLY_CHAIN:
      return ChipColorVariants.GREENISH_YELLOW;

    case EsgCategoryEnum.COMPANY_RESOURCE_USAGE_STRATEGY:
      return ChipColorVariants.PALE_GREEN;

    case EsgCategoryEnum.COMPANY_RESOURCE_INFLOW:
      return ChipColorVariants.ICE_BERG;

    case EsgCategoryEnum.COMPANY_RESOURCE_OUTFLOW:
      return ChipColorVariants.GHOST;

    case EsgCategoryEnum.COMPANY_REPORT_BASICS:
      return ChipColorVariants.JAGGED_ICE;

    case EsgCategoryEnum.COMPANY_BUSINESS_INFORMATION:
      return ChipColorVariants.GRAY;

    case EsgCategoryEnum.COMPANY_FINANCIAL:
      return ChipColorVariants.ALMOND;

    case EsgCategoryEnum.COMPANY_WORKFORCE_STRATEGY:
      return ChipColorVariants.LIGHT_GREEN;

    case EsgCategoryEnum.COMPANY_WORKFORCE_ENGAGEMENT:
      return ChipColorVariants.PURPLE;

    case EsgCategoryEnum.COMPANY_EMPLOYMENT_DIVERSITY:
      return ChipColorVariants.LIGHT_YELLOW;

    case EsgCategoryEnum.COMPANY_REMUNERATION:
      return ChipColorVariants.WATER;

    case EsgCategoryEnum.COMPANY_HEALTH_SAFETY:
      return ChipColorVariants.TEALISH_BLUE;

    case EsgCategoryEnum.COMPANY_HUMAN_RIGHTS:
      return ChipColorVariants.GREENISH_CYAN;

    case EsgCategoryEnum.REFRIGERANTS:
      return ChipColorVariants.GREENISH_YELLOW;

    case EsgCategoryEnum.COMPANY_EMISSION_REDUCTION:
      return ChipColorVariants.PALE_GREEN;

    default:
      return ChipColorVariants.RED;
  }
};

export const getUnitChipColor = (unit: Unit | null) => {
  if (!unit) {
    return ChipColorVariants.LIGHT_YELLOW;
  }
  switch (unit) {
    case Unit.KWH:
      return ChipColorVariants.ALMOND;

    case Unit.ML:
      return ChipColorVariants.PURPLE;

    case Unit.LITRE:
      return ChipColorVariants.LIGHT_GREEN;

    case Unit.MWH:
      return ChipColorVariants.LIGHT_YELLOW;

    case Unit.KG:
      return ChipColorVariants.WATER;

    case Unit.TONNES:
      return ChipColorVariants.TEALISH_BLUE;

    case Unit.HRS:
      return ChipColorVariants.GREENISH_CYAN;

    case Unit.M3:
      return ChipColorVariants.GREENISH_YELLOW;

    case Unit.PERCENTAGE:
      return ChipColorVariants.PALE_GREEN;

    case Unit.EURO:
      return ChipColorVariants.ICE_BERG;

    case Unit.KGCO2KWH:
      return ChipColorVariants.GHOST;

    case Unit.DAYS:
      return ChipColorVariants.JAGGED_ICE;

    case Unit.METER:
      return ChipColorVariants.GRAY;

    case Unit.HA:
      return ChipColorVariants.ALMOND;

    default:
      return ChipColorVariants.RED;
  }
};

export const getProgressBarColor = (progress: number) => {
  if (progress >= 0 && progress < 50) {
    return 'error';
  } else if (progress <= 99) {
    return 'warning';
  } else return 'success';
};

export const getESGAreaChipColor = (area: EsgArea | undefined) => {
  switch (area) {
    case EsgArea.ENVIRONMENT:
      return ChipColorVariants.LIGHT_GREEN;

    case EsgArea.SOCIAL:
      return ChipColorVariants.LIGHT_YELLOW;

    case EsgArea.GOVERNANCE:
      return ChipColorVariants.WATER;
    default:
      return ChipColorVariants.RED;
  }
};

export const getApprovalStatusChipColor = (status: ApprovalStatus) => {
  switch (status) {
    case ApprovalStatus.OPEN:
      return ChipColorVariants.LIGHT_YELLOW;

    case ApprovalStatus.APPROVED:
      return ChipColorVariants.LIGHT_GREEN;

    case ApprovalStatus.REJECTED:
      return ChipColorVariants.ALMOND;

    default:
      return ChipColorVariants.RED;
  }
};

export const getFrameworkChipColor = (framework: ReportingFramework) => {
  switch (framework) {
    case ReportingFramework.GRI:
      return ChipColorVariants.TEALISH_BLUE;

    case ReportingFramework.DNK:
      return ChipColorVariants.LIGHT_GREEN;

    case ReportingFramework.ESRS:
      return ChipColorVariants.LIGHT_YELLOW;

    default:
      return ChipColorVariants.RED;
  }
};

export const getYearChipColor = (year: number) => {
  switch (year) {
    case 2019:
      return ChipColorVariants.GREENISH_YELLOW;

    case 2020:
      return ChipColorVariants.ALMOND;

    case 2021:
      return ChipColorVariants.GREENISH_CYAN;

    case 2022:
      return ChipColorVariants.WATER;

    case 2023:
      return ChipColorVariants.PURPLE;

    case 2024:
      return ChipColorVariants.LIGHT_GREEN;

    case 2025:
      return ChipColorVariants.LIGHT_YELLOW;

    default:
      return ChipColorVariants.RED;
  }
};

export const getFacilityTypeColor = (type: FacilityType) => {
  switch (type) {
    case FacilityType.LOGISTICS:
      return ChipColorVariants.ALMOND;
    case FacilityType.MANUFACTURING:
      return ChipColorVariants.LIGHT_GREEN;
    case FacilityType.SERVICE:
      return ChipColorVariants.PURPLE;
    case FacilityType.OFFICE:
      return ChipColorVariants.LIGHT_YELLOW;
    case FacilityType.WAREHOUSE:
      return ChipColorVariants.WATER;
    default:
      return ChipColorVariants.RED;
  }
};

export const getCollectOverviewStatusChipColor = (type: CollectOverviewStatus) => {
  switch (type) {
    case CollectOverviewStatus.NOT_STARTED:
      return ChipColorVariants.LIGHT_GREY;
    case CollectOverviewStatus.IN_PROGRESS:
      return ChipColorVariants.LIGHT_YELLOW;
    case CollectOverviewStatus.COMPLETED:
      return ChipColorVariants.LIGHT_GREEN;
    case CollectOverviewStatus.OVERDUE:
      return ChipColorVariants.LIGHT_RED;
    case CollectOverviewStatus.INACTIVE:
      return ChipColorVariants.MILK_WHITE;
    default:
      return ChipColorVariants.RED;
  }
};

export const getRequestStatusChipColor = (type: RequestStatus) => {
  switch (type) {
    case RequestStatus.IN_PROGRESS:
      return ChipColorVariants.LIGHT_YELLOW;
    case RequestStatus.COMPLETED:
      return ChipColorVariants.LIGHT_GREEN;
    case RequestStatus.OVERDUE:
      return ChipColorVariants.ALMOND;
    case RequestStatus.NOT_STARTED:
      return ChipColorVariants.LIGHT_GREY;
    default:
      return ChipColorVariants.RED;
  }
};

export const getRoleChipColor = (userRole: UserRole) => {
  switch (userRole) {
    case UserRole.ADMIN:
      return ChipColorVariants.ALMOND;
    case UserRole.LEAD:
      return ChipColorVariants.LIGHT_GREEN;
    case UserRole.EMPLOYEE:
      return ChipColorVariants.WATER;
    case UserRole.EXTERNAL:
      return ChipColorVariants.GRAY;
    case UserRole.STAKEHOLDER:
      return ChipColorVariants.LIGHT_YELLOW;
    case UserRole.VIEWER:
      return ChipColorVariants.GREENISH_YELLOW;
    default:
      return ChipColorVariants.RED;
  }
};

export const getUserGroupCategoryChipColor = (category: UserGroupCategory) => {
  switch (category) {
    case UserGroupCategory.EXTERNAL:
      return ChipColorVariants.GRAY;

    case UserGroupCategory.INTERNAL:
      return ChipColorVariants.LIGHT_ORANGE;

    case UserGroupCategory.SILENT:
      return ChipColorVariants.LIGHT_GREEN;
    default:
      return ChipColorVariants.RED;
  }
};

export const getDataSelectionOverviewStatusChipColor = (status: DataSelectionOverviewStatus) => {
  switch (status) {
    case DataSelectionOverviewStatus.NOT_MATERIAL:
      return ChipColorVariants.LIGHT_GREY;
    case DataSelectionOverviewStatus.MATERIALITY_OPEN:
      return ChipColorVariants.LIGHT_YELLOW;
    case DataSelectionOverviewStatus.COMPLETED:
      return ChipColorVariants.LIGHT_GREEN;
    default:
      return ChipColorVariants.RED;
  }
};

export const getRelatedIroChipColor = (data: RelatedIRO) => {
  const isImpact = data.__typename === 'Impact';
  if (isImpact) return ChipColorVariants.WARM_BLUE;
  else if (data.type === RiskOpportunityType.RISK) return ChipColorVariants.LIGHT_RED;
  else if (data.type === RiskOpportunityType.OPPORTUNITY) return ChipColorVariants.LIGHT_YELLOW;
  else return ChipColorVariants.LIGHT_GREEN;
};

export const getESRSChapterLabelChipColor = (chapterLabel: string) => {
  if (!chapterLabel) {
    return ChipColorVariants.LIGHT_YELLOW;
  }

  switch (chapterLabel) {
    case 'ESRS2':
      return ChipColorVariants.ALMOND;

    case 'E1':
      return ChipColorVariants.PURPLE;

    case 'E2':
      return ChipColorVariants.LIGHT_GREEN;

    case 'E3':
      return ChipColorVariants.LIGHT_YELLOW;

    case 'E4':
      return ChipColorVariants.WATER;

    case 'E5':
      return ChipColorVariants.TEALISH_BLUE;

    case 'S1':
      return ChipColorVariants.GREENISH_CYAN;

    case 'S2':
      return ChipColorVariants.GREENISH_YELLOW;

    case 'S3':
      return ChipColorVariants.PALE_GREEN;

    case 'S4':
      return ChipColorVariants.ICE_BERG;

    case 'G1':
      return ChipColorVariants.GHOST;

    default:
      return ChipColorVariants.RED;
  }
};

export const getEsgKpiIROStatusChipColor = (status: EsgKpiStatus): ChipColorVariants => {
  switch (status) {
    case EsgKpiStatus.MANDATORY:
      return ChipColorVariants.ALMOND;
    case EsgKpiStatus.RELEVANT:
      return ChipColorVariants.LIGHT_YELLOW;
    case EsgKpiStatus.OPTIONAL:
      return ChipColorVariants.LIGHT_GREEN;
    case EsgKpiStatus.UNDEFINED:
      return ChipColorVariants.GRAY;
    default:
      return ChipColorVariants.RED;
  }
};

export const getConversationStatusChipColor = (status: ConversationStatus) => {
  switch (status) {
    case ConversationStatus.WAITING_FOR_INPUT:
      return ChipColorVariants.LIGHT_YELLOW;
    case ConversationStatus.PROCESSING:
      return ChipColorVariants.TEALISH_BLUE;
    case ConversationStatus.COMPLETED:
      return ChipColorVariants.LIGHT_GREEN;
    default:
      return ChipColorVariants.RED;
  }
};
